<template>
    <div>
        <b-dropdown
            ref="dropdown"
            class="lx-date-range-picker mr-1"
            :disabled="disabled"
            :variant="variant"
            @show="handleDropdownShow"
            @hide="handleDropdownHide"
            @hidden="handleDropdownHidden"
        >
            <template v-slot:button-content>
                <font-awesome-icon
                    :icon="['fas', 'calendar-days']"
                    size="lg"
                ></font-awesome-icon>
                <span
                    class="ml-2"
                    :class="{
                        'lx-date-range-picker__label': isResponsive,
                    }"
                >
                    {{ formattedDateRange }}
                </span>
            </template>
            <b-dropdown-form>
                <div class="lx-date-range-picker__container">
                    <div class="lx-date-range-picker__buttons-col">
                        <b-button
                            class="lx-date-range-picker__button"
                            :class="{
                                'lx-date-range-picker__button-custom-range': !isCustomRange,
                            }"
                            v-for="btn in rangeButtons"
                            variant="outline-primary"
                            :key="btn.range.name"
                            :pressed="btn.range.name === currentValue.name"
                            @click="onRangeButtonClick(btn)"
                        >
                            {{ btn.caption }}
                        </b-button>
                        <b-button
                            v-if="withExpand"
                            class="lx-date-range-picker__expand-collapse d-flex justify-content-center"
                            variant="outline-primary"
                            @click="rangeButtonsExpanded = !rangeButtonsExpanded"
                        >
                            <font-awesome-icon
                                size="lg"
                                :icon="
                                    rangeButtonsExpanded ? ['fas', 'circle-chevron-up'] : ['fas', 'circle-chevron-down']
                                "
                            ></font-awesome-icon>
                        </b-button>
                        <div
                            class="lx-date-range-picker__operation"
                            v-bind:class="{
                                'lx-date-range-picker__button-custom-range': !isCustomRange,
                            }"
                        >
                            <b-button
                                class="lx-date-range-picker__operation-button"
                                @click="onApplyButtonClick"
                                variant="success"
                                >Apply</b-button
                            >
                            <b-button
                                class="lx-date-range-picker__operation-button"
                                @click="onCancelButtonClick"
                                variant="light"
                                >Cancel</b-button
                            >
                        </div>
                    </div>
                    <div
                        class="lx-date-range-picker__calendars-col"
                        v-show="isCustomRange"
                    >
                        <b-col>
                            <header
                                class="lx-date-range-picker__calendar-header"
                            >
                                <span>Start Date</span>
                            </header>
                            <b-calendar
                                v-bind="startAttrs"
                                v-model="startDate"
                                :max="endDate"
                                :date-info-fn="dateInfoFn"
                            ></b-calendar>
                        </b-col>
                        <b-col>
                            <header
                                class="lx-date-range-picker__calendar-header"
                            >
                                <span>End Date</span>
                            </header>
                            <b-calendar
                                v-bind="endAttrs"
                                v-model="endDate"
                                :min="startDate"
                                :max="maxEndDate"
                                :date-info-fn="dateInfoFn"
                            ></b-calendar>
                        </b-col>
                    </div>
                </div>
            </b-dropdown-form>
        </b-dropdown>
    </div>
</template>

<script>
    import addDays from 'date-fns/addDays';
    import addMonths from 'date-fns/addMonths';
    import addWeeks from 'date-fns/addWeeks';
    import addYears from 'date-fns/addYears';
    import differenceInMonths from 'date-fns/differenceInMonths';
    import endOfMonth from 'date-fns/endOfMonth';
    import endOfWeek from 'date-fns/endOfWeek';
    import endOfYear from 'date-fns/endOfYear';
    import format from 'date-fns/format';
    import isSameDay from 'date-fns/isSameDay';
    import isWithinInterval from 'date-fns/isWithinInterval';
    import parseISO from 'date-fns/parseISO';
    import startOfMonth from 'date-fns/startOfMonth';
    import startOfWeek from 'date-fns/startOfWeek';
    import startOfYear from 'date-fns/startOfYear';
    import subDays from 'date-fns/subDays';
    import subMonths from 'date-fns/subMonths';
    import subWeeks from 'date-fns/subWeeks';
    import subYears from 'date-fns/subYears';
    import isArray from 'lodash/isArray';

    export const LAST_N_DAYS_7 = 'last 7 days';
    export const LAST_N_DAYS_14 = 'last 14 days';
    export const LAST_N_DAYS_30 = 'last 30 days';
    export const YESTERDAY = 'yesterday';
    export const TODAY = 'today';
    export const TOMORROW = 'tomorrow';
    export const LAST_WEEK = 'last week';
    export const THIS_WEEK = 'this week';
    export const NEXT_WEEK = 'next week';
    export const LAST_MONTH = 'last month';
    export const THIS_MONTH = 'this month';
    export const NEXT_MONTH = 'next month';
    export const LAST_YEAR = 'last year';
    export const LAST_N_YEARS_0 = `${new Date().getFullYear()}`;
    export const LAST_N_YEARS_1 = `${new Date().getFullYear() - 1}`;
    export const LAST_N_YEARS_2 = `${new Date().getFullYear() - 2}`;
    export const LAST_N_YEARS_3 = `${new Date().getFullYear() - 3}`;
    export const LAST_N_YEARS_4 = `${new Date().getFullYear() - 4}`;
    export const LAST_N_YEARS_5 = `${new Date().getFullYear() - 5}`;
    export const LAST_N_YEARS_6 = `${new Date().getFullYear() - 6}`;
    export const LAST_N_YEARS_7 = `${new Date().getFullYear() - 7}`;
    export const LAST_N_YEARS_8 = `${new Date().getFullYear() - 8}`;
    export const LAST_N_YEARS_9 = `${new Date().getFullYear() - 9}`;
    export const LAST_N_YEARS_10 = `${new Date().getFullYear() - 10}`;
    export const THIS_YEAR = 'this year';
    export const NEXT_YEAR = 'next year';
    export const NEXT_THREE_MONTHS = 'next three months';
    export const NEXT_SIX_MONTHS = 'next six months';
    export const PAST = 'past';
    export const FUTURE = 'future';
    export const ALL_DATES = 'all dates';
    export const CUSTOM_RANGE = 'custom range';
    export const FROM_START_OF_LAST_MONTH_TO_END_OF_THIS_MONTH = 'from_start_of_last_month_to_end_of_this_month'
    export const NEXT_N_DAYS_7 = 'next 7 days';
    export const NEXT_N_DAYS_14 = 'next 14 days';
    export const NEXT_N_DAYS_30 = 'next 30 days';


    export function nameToRange(name) {
        let from, to;
        const today = new Date();
        if (name === YESTERDAY) {
            from = to = subDays(today, 1);
        } else if (name === TODAY) {
            from = to = today;
        } else if (name === TOMORROW) {
            from = to = addDays(today, 1);
        } else if (name === LAST_WEEK) {
            const lastWeek = subWeeks(today, 1);
            from = startOfWeek(lastWeek);
            to = endOfWeek(lastWeek);
        } else if (name === THIS_WEEK) {
            from = startOfWeek(today);
            to = endOfWeek(today);
        } else if (name === NEXT_WEEK) {
            const nextWeek = addWeeks(today, 1);
            from = startOfWeek(nextWeek);
            to = endOfWeek(nextWeek);
        } else if (name === LAST_N_DAYS_7) {
            from = subDays(today, 7);
            to = today;
        } else if (name === NEXT_N_DAYS_7) {
            from = today;
            to = addDays(today, 7);
        } else if (name === LAST_N_DAYS_14) {
            from = subDays(today, 14);
            to = today;
        } else if (name === NEXT_N_DAYS_14) {
            from = today;
            to = addDays(today, 14);
        } else if (name === LAST_N_DAYS_30) {
            from = subDays(today, 30);
            to = today;
        } else if (name === NEXT_N_DAYS_30) {
            from = today;
            to = addDays(today, 30);
        } else if (name === LAST_MONTH) {
            const lastMonth = subMonths(today, 1);
            from = startOfMonth(lastMonth);
            to = endOfMonth(lastMonth);
        } else if (name === THIS_MONTH) {
            from = startOfMonth(today);
            to = endOfMonth(today);
        } else if (name === NEXT_MONTH) {
            const nextMonth = addMonths(today, 1);
            from = startOfMonth(nextMonth);
            to = endOfMonth(nextMonth);
        } else if (name === NEXT_THREE_MONTHS) {
            from = startOfMonth(addMonths(today, 1));
            to = endOfMonth(addMonths(today, 3));
        } else if (name === NEXT_SIX_MONTHS) {
            from = startOfMonth(addMonths(today, 1));
            to = endOfMonth(addMonths(today, 6));
        } else if (name === LAST_YEAR) {
            const lastYear = subYears(today, 1);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === THIS_YEAR) {
            from = startOfYear(today);
            to = endOfYear(today);
        } else if (name === NEXT_YEAR) {
            const nextYear = addYears(today, 1);
            from = startOfYear(nextYear);
            to = endOfYear(nextYear);
        } else if (name === PAST) {
            from = startOfYear(subYears(today, 20));
            to = today;
        } else if (name === FUTURE) {
            from = today;
            to = endOfYear(addYears(today, 20));
        } else if (name === ALL_DATES) {
            from = startOfYear(subYears(today, 20));
            to = endOfYear(addYears(today, 20));
        } else if (name === FROM_START_OF_LAST_MONTH_TO_END_OF_THIS_MONTH) {
            const lastMonth = subMonths(today, 1);
            from = startOfMonth(lastMonth);
            to = endOfMonth(today);
        } else if (name === LAST_N_YEARS_0) {
            const lastYear = subYears(today, 0);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_1) {
            const lastYear = subYears(today, 1);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_2) {
            const lastYear = subYears(today, 2);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_3) {
            const lastYear = subYears(today, 3);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_4) {
            const lastYear = subYears(today, 4);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_5) {
            const lastYear = subYears(today, 5);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_6) {
            const lastYear = subYears(today, 6);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_7) {
            const lastYear = subYears(today, 7);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_8) {
            const lastYear = subYears(today, 8);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_9) {
            const lastYear = subYears(today, 9);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        } else if (name === LAST_N_YEARS_10) {
            const lastYear = subYears(today, 10);
            from = startOfYear(lastYear);
            to = endOfYear(lastYear);
        }
        return { name, from, to };
    }

    export default {
        name: 'DateRangePicker',
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            variant: {
                type: String,
                default: 'primary',
            },
            isResponsive: {
                type: Boolean,
                default: true,
            },
            rangeOptions: {
                type: [Array, Object],
                default: () => [],
            },
            disableCustomRange: {
                type: Boolean,
                default: false,
            },
            rangeMonthsLimit: {
                type: Number,
                default: null,
            },
            value: {
                type: Object,
                default: {},
            },
            dateFormat: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                buttonDefinitions: [
                    {
                        caption: 'Yesterday',
                        range: nameToRange(YESTERDAY),
                    },
                    {
                        caption: 'Today',
                        range: nameToRange(TODAY),
                    },
                    {
                        caption: 'Tomorrow',
                        range: nameToRange(TOMORROW),
                    },
                    {
                        caption: 'Last Week',
                        range: nameToRange(LAST_WEEK),
                    },
                    {
                        caption: 'This Week',
                        range: nameToRange(THIS_WEEK),
                    },
                    {
                        caption: 'Next Week',
                        range: nameToRange(NEXT_WEEK),
                    },
                    {
                        caption: 'Last 7 Days',
                        range: nameToRange(LAST_N_DAYS_7),
                    },
                    {
                        caption: 'Next 7 Days',
                        range: nameToRange(NEXT_N_DAYS_7),
                    },
                    {
                        caption: 'Last 14 Days',
                        range: nameToRange(LAST_N_DAYS_14),
                    },
                    {
                        caption: 'Next 14 Days',
                        range: nameToRange(NEXT_N_DAYS_14),
                    },
                    {
                        caption: 'Last Month',
                        range: nameToRange(LAST_MONTH),
                    },
                    {
                        caption: 'This Month',
                        range: nameToRange(THIS_MONTH),
                    },
                    {
                        caption: 'Next Month',
                        range: nameToRange(NEXT_MONTH),
                    },
                    {
                        caption: 'Last 30 Days',
                        range: nameToRange(LAST_N_DAYS_30),
                    },
                    {
                        caption: 'Next 30 Days',
                        range: nameToRange(NEXT_N_DAYS_30),
                    },
                    {
                        caption: 'Next Three Months',
                        range: nameToRange(NEXT_THREE_MONTHS),
                    },
                    {
                        caption: 'Next Six Months',
                        range: nameToRange(NEXT_SIX_MONTHS),
                    },
                    {
                        caption: `${new Date().getFullYear() - 10}`,
                        range: nameToRange(LAST_N_YEARS_10),
                    },
                    {
                        caption: `${new Date().getFullYear() - 9}`,
                        range: nameToRange(LAST_N_YEARS_9),
                    },
                    {
                        caption: `${new Date().getFullYear() - 8}`,
                        range: nameToRange(LAST_N_YEARS_8),
                    },
                    {
                        caption: `${new Date().getFullYear() - 7}`,
                        range: nameToRange(LAST_N_YEARS_7),
                    },
                    {
                        caption: `${new Date().getFullYear() - 6}`,
                        range: nameToRange(LAST_N_YEARS_6),
                    },
                    {
                        caption: `${new Date().getFullYear() - 5}`,
                        range: nameToRange(LAST_N_YEARS_5),
                    },
                    {
                        caption: `${new Date().getFullYear() - 4}`,
                        range: nameToRange(LAST_N_YEARS_4),
                    },
                    {
                        caption: `${new Date().getFullYear() - 3}`,
                        range: nameToRange(LAST_N_YEARS_3),
                    },
                    {
                        caption: `${new Date().getFullYear() - 2}`,
                        range: nameToRange(LAST_N_YEARS_2),
                    },
                    {
                        caption: `${new Date().getFullYear() - 1}`,
                        range: nameToRange(LAST_N_YEARS_1),
                    },
                    {
                        caption: `${new Date().getFullYear()}`,
                        range: nameToRange(LAST_N_YEARS_0),
                    },
                    {
                        caption: 'Last Year',
                        range: nameToRange(LAST_YEAR),
                    },
                    {
                        caption: 'This Year',
                        range: nameToRange(THIS_YEAR),
                    },
                    {
                        caption: 'Next Year',
                        range: nameToRange(NEXT_YEAR),
                    },
                    {
                        caption: 'Past',
                        range: nameToRange(PAST),
                    },
                    {
                        caption: 'Future',
                        range: nameToRange(FUTURE),
                    },
                    {
                        caption: 'All Dates',
                        range: nameToRange(ALL_DATES),
                    },
                    {
                        caption: 'Custom Range',
                        range: {
                            name: CUSTOM_RANGE,
                        },
                    },
                ],
                currentValue: null,
                previousValue: null,
                rangeButtonsExpanded: false,
            };
        },
        computed: {
            withExpand() {
                return !isArray(this.rangeOptions);
            },
            rangeButtons() {
                const rangeOptions = this.withExpand
                    ? this.rangeButtonsExpanded
                        ? this.rangeOptions.expanded
                        : this.rangeOptions.collapsed
                    : this.rangeOptions;
                const optionNames = rangeOptions.map((v) => v.toLowerCase());
                const buttons = [];
                if (optionNames.length) {
                    if (!this.disableCustomRange && (!this.withExpand || this.rangeButtonsExpanded)) {
                        optionNames.push(CUSTOM_RANGE);
                    }
                    buttons.push(
                        ...this.buttonDefinitions.filter(
                            (v) => optionNames.indexOf(v.range.name) != -1
                        )
                    );
                }
                return buttons;
            },
            isCustomRange() {
                return this.currentValue?.name === CUSTOM_RANGE;
            },
            startDate: {
                get() {
                    return this.currentValue?.from;
                },
                set(val) {
                    const startDate = typeof val === 'string' ? parseISO(val) : val;

                    if (
                        startDate
                        && this.endDate
                        && this.rangeMonthsLimit
                        && differenceInMonths(this.endDate, startDate) > this.rangeMonthsLimit
                    ) {
                        this.endDate = addMonths(startDate, this.rangeMonthsLimit);
                    }

                    this.currentValue = {
                        ...this.currentValue,
                        from: startDate,
                    };
                },
            },
            endDate: {
                get() {
                    return this.currentValue?.to;
                },
                set(val) {
                    this.currentValue = {
                        ...this.currentValue,
                        to: typeof val === 'string' ? parseISO(val) : val,
                    };
                },
            },
            maxEndDate() {
                if (this.startDate && this.rangeMonthsLimit) {
                    return addMonths(this.startDate, this.rangeMonthsLimit);
                }
            },
            startAttrs() {
                // prefix attrs with 'calendar-start-'
                const prefix = 'calendar-start-';
                return {
                    'hide-header': true,
                    ...this.parseAttrsFor(prefix),
                    id: `${prefix}${this.$attrs.id}`,
                };
            },
            endAttrs() {
                // prefix attrs with 'calendar-end-'
                const prefix = 'calendar-end-';
                return {
                    'hide-header': true,
                    ...this.parseAttrsFor(prefix),
                    id: `${prefix}${this.$attrs.id}`,
                };
            },
            formattedDateRange() {
                if (!this.startDate || !this.endDate || !this.dateFormat) {
                    return '';
                }
                const formattedStart = format(this.startDate, this.dateFormat);
                const formattedEnd = format(this.endDate, this.dateFormat);
                return `${formattedStart} - ${formattedEnd}`;
            },
        },
        methods: {
            parseAttrsFor(calendarType) {
                const regex = new RegExp(calendarType);
                return Object.keys(this.$attrs).reduce((attrs, key) => {
                    if (key.indexOf(calendarType) != -1) {
                        attrs[key.replace(regex, '')] = this.$attrs[key];
                    }
                    return attrs;
                }, {});
            },
            dateInfoFn(ymd, date) {
                const classNames = [];
                if (date && this.startDate && this.endDate) {
                    if (
                        isWithinInterval(date, {
                            start: this.startDate,
                            end: this.endDate,
                        })
                    ) {
                        classNames.push('table-info');
                        if (isSameDay(date, this.startDate)) {
                            classNames.push('round-cell-left');
                        }
                        if (isSameDay(date, this.endDate)) {
                            classNames.push('round-cell-right');
                        }
                    }
                }
                return classNames;
            },
            onRangeButtonClick(button) {
                if (button.range.name === CUSTOM_RANGE) {
                    if (this.currentValue.name == ALL_DATES) {
                        this.currentValue = nameToRange(THIS_YEAR);
                    }
                    this.currentValue = {
                        ...this.currentValue,
                        name: CUSTOM_RANGE,
                    };
                } else {
                    this.currentValue = { ...button.range };
                    this.onApplyButtonClick();
                }
            },
            onApplyButtonClick() {
                this.previousValue = { ...this.currentValue };
                this.$emit('input', { ...this.currentValue });
                this.$refs.dropdown.hide(true);
            },
            onCancelButtonClick() {
                this.$refs.dropdown.hide(true);
            },
            handleDropdownShow(e) {
                this.$emit('show', e);
            },
            handleDropdownHide(e) {
                this.$emit('hide', e);
            },
            handleDropdownHidden() {
                this.currentValue = { ...this.previousValue };
            },
        },
        created() {
            this.currentValue = this.value;
            const buttonName = this.currentValue?.name;
            if (buttonName && buttonName !== CUSTOM_RANGE) {
                const buttonDef = this.buttonDefinitions.filter(
                    (v) => v.range.name === buttonName
                );
                if (buttonDef.length) {
                    this.currentValue = { ...buttonDef[0].range };
                } else {
                    this.currentValue = {
                        ...this.currentValue,
                        name: CUSTOM_RANGE,
                    };
                }
            }
            this.previousValue = { ...this.currentValue };
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    $cell-round-border: 50%;
    $selected-button-color: #08c;
    $primary-color: #0d47a1;
    $max-viewport-height: 60vh;
    $button-height: 28px;

    :deep(.b-dropdown-form) {
        padding: 0.25rem 0.5rem !important;
    }

    .primary {
        color: #fff !important;
        background-color: $primary-color !important;
        border-color: $primary-color !important;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
            0 1px 1px rgba(0, 0, 0, 0.075) !important;
        transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
            border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;

        &:hover {
            background-color: #0a377e !important;
            border-color: #093272 !important;
        }

        > :deep(label) {
            color: #fff !important;
        }
    }

    .b-form-btn-label-control.form-control :deep(.dropdown-menu) {
        padding: 0.8rem !important;
    }

    :deep(.round-cell-right) {
        border-bottom-right-radius: $cell-round-border;
        border-top-right-radius: $cell-round-border;
    }

    :deep(.round-cell-left) {
        border-bottom-left-radius: $cell-round-border;
        border-top-left-radius: $cell-round-border;
    }

    :deep(span.btn-light) {
        background-color: inherit !important;
        box-shadow: none !important;
    }

    :deep(.col) {
        padding: 3px !important;
    }

    :deep(.dropdown-toggle) {
        &.btn-default {
            color: #495057 !important;
            background-color: #fff !important;
            border-color: #ced4da !important;
            box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%) !important;
            &:hover {
                color: #212529 !important;
                background-color: #fff !important;
                border-color: #ced4da !important;
                box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%) !important;
            }
        }
    }

    :deep(.dropdown-menu) {
        max-height: $max-viewport-height;
        overflow-y: auto;
    }

    :deep(label) {
        user-select: none !important;
        box-shadow: none !important;
        display: flex !important;
        justify-content: space-between;
        align-items: center;

        &:after {
            display: inline-block;
            margin-left: 0.255em;
            vertical-align: 0.255em;
            content: '';
            border-top: 0.3em solid;
            border-right: 0.3em solid transparent;
            border-bottom: 0;
            border-left: 0.3em solid transparent;
        }
    }

    :deep(footer) {
        display: none !important;
    }

    :deep(.col) {
        padding: 0px !important;
        width: max-content !important;
    }

    .lx-date-range-picker {
        &__container {
            display: flex;
            height: 100%;
        }

        &__buttons-col {
            display: flex;
            flex-direction: column;
        }

        &__calendars-col {
            display: flex;
        }

        &__calendar-header {
            display: flex;
            align-items: center;
            justify-content: center;
            height: $button-height;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
            margin-bottom: 5px;
            color: #495057;
            box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
        }

        &__button {
            width: 130px;
            height: $button-height;
            margin: 1px;
            line-height: 12px !important;
            font-size: 13px !important;
            text-align: left !important;
            background: #f5f5f5 !important;
            border: 1px solid #f5f5f5 !important;
            color: $selected-button-color !important;
            margin-bottom: 8px;
            box-shadow: unset !important;
            margin-right: 5px;

            &:hover {
                background: $selected-button-color !important;
                border: 1px solid $selected-button-color !important;
                color: #fff !important;
            }

            &-custom-range {
                margin-right: 1px;
            }
        }

        &__button.active {
            background: $selected-button-color !important;
            border: 1px solid $selected-button-color !important;
            color: #fff !important;
        }

        &__expand-collapse {
            width: 130px;
            height: $button-height;
            margin: 1px;
            line-height: 12px !important;
            font-size: 13px !important;
            text-align: left !important;
            color: $selected-button-color !important;
            border: 0 !important;
            margin-bottom: 8px;
            box-shadow: unset !important;
            margin-right: 5px;

            &:hover {
                background: $selected-button-color !important;
                border: 0 !important;
                color: #fff !important;
            }
        }

        &__operation {
            display: flex;
            justify-content: space-between;
            margin-right: 5px;
            margin-top: auto;

            &-button {
                height: $button-height;
                margin: 1px;
                line-height: 12px !important;
                font-size: 13px !important;
                box-shadow: unset !important;
                border: 1px solid #f5f5f5 !important;
            }
        }
    }

    @include media-breakpoint-down(md) {
        .lx-date-range-picker {
            &__button {
                font-size: 14px !important;

                &-custom-range {
                    margin: 3px;
                }
            }

            &__operation {
                width: 100%;
                align-items: center;
                justify-content: space-around;
                margin: 0px !important;

                &-button {
                    font-size: 14px !important;
                }
            }

            &__container {
                flex-direction: column;
                width: min-content !important;
            }

            &__buttons-col {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                justify-content: center;
                flex-basis: unset !important;
            }

            &__calendars-col {
                margin: 5px;
                height: max-content;
                border-left: none;
                padding: 0px;
                flex-direction: column;

                > div {
                    margin-top: 5px;
                    margin-bottom: 5px;
                    padding-top: 5px !important;
                    border-top: 1px solid #d8d8d8;
                }
            }

            &__label {
                display: none;
            }
        }
    }
</style>
